import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext'; 
import { logAuditAction } from '../../lib/auditLogger'; 
import Spinner from '../../components/Spinner';
import Skeleton from '../../components/Skeleton';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';
import StarterSpreadsheetEditor from '../../components/editors/StarterSpreadsheetEditor';
import {validateStarterQuestion} from '../../utils/starterSpreadsheet';

// --- Helper Component: Modal to Select Existing Questions (Multi-Select) ---
const SubQuestionImportModal = ({ questions, currentQuestionId, existingSubQuestions, onImport, onClose }) => {
    // 1. Identify IDs already present in the Case Study to prevent duplicates
    const importedSourceIds = existingSubQuestions
        .map(sq => sq.sourceId) 
        .filter(Boolean);

    // 2. Filter available questions
    // - Exclude Case Studies (no nesting)
    // - Exclude already imported questions
    const availableQuestions = questions.filter(q => 
        q.type !== 'case-study' &&
        !importedSourceIds.includes(q.id)
    );

    // 3. States
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTopic, setFilterTopic] = useState('');
    const [filterSection, setFilterSection] = useState('');

    // 4. Extract unique topics for dropdown
    const uniqueTopics = [...new Set(availableQuestions.map(q => q.topic).filter(Boolean))].sort();

    // 5. Apply Filters
    const filtered = availableQuestions.filter(q => {
        const matchesText = (q.text || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTopic = filterTopic ? q.topic === filterTopic : true;
        const matchesSection = filterSection ? q.section === filterSection : true;
        return matchesText && matchesTopic && matchesSection;
    });

    // Handlers
    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleImportClick = () => {
        const selectedQuestions = availableQuestions.filter(q => selectedIds.includes(q.id));
        onImport(selectedQuestions);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="text-xl font-bold text-gray-800">Import Existing Questions</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 font-bold text-2xl">&times;</button>
                </div>
                
                {/* Filters Container */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 bg-gray-50 p-3 rounded border">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Search Text</label>
                        <input 
                            type="text" 
                            placeholder="e.g. Calculate ratio..." 
                            className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-purple-400 outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Topic</label>
                        <select 
                            value={filterTopic} 
                            onChange={(e) => setFilterTopic(e.target.value)} 
                            className="w-full p-2 border rounded text-sm bg-white"
                        >
                            <option value="">All Topics</option>
                            {uniqueTopics.map((topic, idx) => (
                                <option key={idx} value={topic}>{topic}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter by Section</label>
                        <select 
                            value={filterSection} 
                            onChange={(e) => setFilterSection(e.target.value)} 
                            className="w-full p-2 border rounded text-sm bg-white"
                        >
                            <option value="">All Sections</option>
                            <option value="A">Section A</option>
                            <option value="B">Section B</option>
                            <option value="C">Section C</option>
                        </select>
                    </div>
                </div>

                {/* Results List */}
                <div className="flex-1 overflow-y-auto space-y-2 pr-2 border-t pt-2">
                    {filtered.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No matching questions found (or already imported).</p>
                        </div>
                    ) : (
                        filtered.map(q => (
                            <div 
                                key={q.id} 
                                className={`border p-3 rounded cursor-pointer flex justify-between items-start transition-colors ${selectedIds.includes(q.id) ? 'bg-purple-50 border-purple-400' : 'hover:bg-gray-50'}`} 
                                onClick={() => toggleSelect(q.id)}
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedIds.includes(q.id)} 
                                        onChange={() => toggleSelect(q.id)}
                                        className="h-5 w-5 text-purple-600 rounded"
                                    />
                                    <div className="flex-1">
                                        <div className="flex flex-wrap gap-2 mb-1 items-center">
                                            {/* Badge if it's the current question being edited */}
                                            {q.id === currentQuestionId && (
                                                <span className="text-[10px] font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded uppercase">
                                                    CURRENT
                                                </span>
                                            )}
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                                                q.type === 'mcq' ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-800'
                                            }`}>
                                                {q.type}
                                            </span>
                                            <span className="text-[10px] font-bold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                                Sec {q.section}
                                            </span>
                                            <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                                {q.topic}
                                            </span>
                                            <span className="text-xs text-gray-500 ml-auto">({q.marks} Marks)</span>
                                        </div>
                                        <div className="text-sm text-gray-800 line-clamp-1" dangerouslySetInnerHTML={{ __html: q.text || '(Image Only Question)' }} />
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer Actions */}
                <div className="mt-4 border-t pt-4 flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600">
                        {selectedIds.length} question{selectedIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded font-bold hover:bg-gray-300">
                            Cancel
                        </button>
                        <button 
                            onClick={handleImportClick} 
                            disabled={selectedIds.length === 0}
                            className="px-4 py-2 bg-purple-600 text-white rounded font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Import Selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const QuestionBankManager = ({ paper, onBack, setModal }) => {
    const { user } = useAuth(); 

    // --- State: Navigation ---
    const [viewMode, setViewMode] = useState('topics'); 
    const [selectedTopic, setSelectedTopic] = useState(null);

    // --- State: Data ---
    const [topics, setTopics] = useState([]);
    const [questions, setQuestions] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);

    // --- State: Filters & UI ---
    const [newTopicName, setNewTopicName] = useState('');
    const [filterSection, setFilterSection] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [editingQuestionId, setEditingQuestionId] = useState(null); 
    const [showImportModal, setShowImportModal] = useState(false); 

    // --- State: Question Editor ---
    const defaultQuestionState = {
        topic: '', 
        topicId: '', 
        section: 'A',
        type: 'mcq',
        text: '', 
        marks: 1,
        // Common Arrays
        options: ['', '', '', ''],
        // Type Specific
        prompts: ['Prompt 1', 'Prompt 2'],
        rows: ['Row 1'],
        columns: ['Column 1'],
        // Answers
        correctAnswer: [], 
        // Assets
        images: [],
        // Explanations
        explanation: '',
        explanationFormat: 'text',
        // Descriptive Specific
        suggestedAnswerContent: '',
        starterSpreadsheet: null,
        suggestedAnswerExcel: null,
        suggestedAnswerFormat: 'text',
        markingSchemeContent: '',
        markingSchemeFormat: 'text',
        // --- Case Study Specific ---
        subQuestions: [] 
    };
    
    const [newQuestion, setNewQuestion] = useState(defaultQuestionState);

    // --- EFFECT 1: Fetch Topics ---
    useEffect(() => {
        const q = query(collection(db, "topics"), where("paperId", "==", paper.id));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => a.name.localeCompare(b.name));
            setTopics(data);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [paper.id]);

    // --- EFFECT 2: Fetch Questions ---
    useEffect(() => {
        const q = query(
            collection(db, "questionBank"), 
            where("paperId", "==", paper.id)
        );
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setQuestions(data);
        });
        return () => unsubscribe();
    }, [paper.id]);


    // --- Actions: Topic Management ---

    const handleCreateTopic = async (e) => {
        e.preventDefault();
        if (!newTopicName.trim()) return;
        try {
            const docRef = await addDoc(collection(db, "topics"), {
                name: newTopicName,
                paperId: paper.id,
                createdAt: serverTimestamp()
            });
            logAuditAction(user, 'CREATE_TOPIC', `Created topic folder '${newTopicName}' in ${paper.name}`, { id: docRef.id, name: newTopicName });
            setNewTopicName('');
        } catch (error) {
            console.error("Error creating topic:", error);
            setModal({ show: true, message: error.message });
        }
    };

    const handleDeleteTopic = async (topicId, e) => {
        e.stopPropagation(); 
        if(!window.confirm("Are you sure you want to delete this topic? Questions linked to it will remain in the database but may become orphaned.")) return;
        
        const topicToDelete = topics.find(t => t.id === topicId);

        try {
            await deleteDoc(doc(db, "topics", topicId));
            logAuditAction(user, 'DELETE_TOPIC', `Deleted topic folder '${topicToDelete?.name}' from ${paper.name}`, { id: topicId });
        } catch (error) {
            setModal({ show: true, message: error.message });
        }
    };

    const handleEnterTopic = (topic) => {
        setSelectedTopic(topic);
        setViewMode('questions');
        resetEditor(); 
        setFilterSection('');
    };

    const resetEditor = () => {
        setNewQuestion(defaultQuestionState);
        setIsCreating(false);
        setEditingQuestionId(null);
    };

    // --- Actions: Question Management ---

    const handleSaveQuestion = async () => {
        const starterErrors=validateStarterQuestion(newQuestion);
        if(starterErrors.length){setModal({show:true,message:starterErrors.slice(0,5).join('\n')});return;}
        // Validation
        if (newQuestion.type === 'mcq' && (!newQuestion.correctAnswer || newQuestion.correctAnswer.length === 0)) {
            setModal({ show: true, message: "Please select at least one correct answer for MCQ." });
            return;
        }
        if (newQuestion.type === 'short-answer' && !newQuestion.correctAnswer.trim()) {
            setModal({ show: true, message: "Please enter the correct answer text." });
            return;
        }

        try {
            // Recalculate marks for case studies
            let calculatedMarks = Number(newQuestion.marks) || 0;
            if (newQuestion.type === 'case-study' && newQuestion.subQuestions?.length > 0) {
                calculatedMarks = newQuestion.subQuestions.reduce((sum, sq) => sum + (Number(sq.marks) || 0), 0);
            }

            const questionData = {
                ...newQuestion,
                topic: selectedTopic.name,
                topicId: selectedTopic.id,
                marks: calculatedMarks,
                paperId: paper.id,
                createdAt: editingQuestionId ? newQuestion.createdAt : serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            if (editingQuestionId) {
                await updateDoc(doc(db, "questionBank", editingQuestionId), questionData);
                logAuditAction(user, 'EDIT_QUESTION', `Edited question in topic '${selectedTopic.name}'`, { id: editingQuestionId });
                setModal({ show: true, message: "Question updated successfully!" });
            } else {
                const docRef = await addDoc(collection(db, "questionBank"), questionData);
                logAuditAction(user, 'ADD_QUESTION', `Added new ${newQuestion.type} question to '${selectedTopic.name}'`, { id: docRef.id });
                setModal({ show: true, message: "Question added to bank successfully!" });
            }
            
            resetEditor();
        } catch (error) {
            console.error("Error saving question:", error);
            setModal({ show: true, message: `Error: ${error.message}` });
        }
    };

    const handleEditQuestion = (q) => {
        setNewQuestion({ ...defaultQuestionState, ...q });
        setEditingQuestionId(q.id);
        setIsCreating(true);
    };

    const handleDeleteQuestion = async (id) => {
        if(!window.confirm("Delete this question from the bank?")) return;
        try {
            await deleteDoc(doc(db, "questionBank", id));
            logAuditAction(user, 'DELETE_QUESTION', `Deleted question from topic '${selectedTopic.name}'`, { id });
        } catch(e) {
            setModal({show:true, message: e.message});
        }
    };

    // --- Sub-Component: The Question Editor ---
    const renderQuestionEditor = () => {
        const q = newQuestion;
        const updateQ = (field, value) => setNewQuestion(prev => ({ ...prev, [field]: value }));

        const handleArrayChange = (field, index, value) => {
            const newArray = [...q[field]]; newArray[index] = value; updateQ(field, newArray);
        };
        const addItem = (field) => updateQ(field, [...q[field], `New ${field.slice(0, -1)}`]);
        const removeItem = (field, index) => { const newArray = [...q[field]]; newArray.splice(index, 1); updateQ(field, newArray); };

        const handleTypeChange = (newType) => {
            let resetData = { type: newType };
            if (newType === 'mcq') resetData = { ...resetData, marks: 1, correctAnswer: [], options: ['', '', '', ''] };
            else if (newType === 'dropdown') resetData = { ...resetData, marks: 1, correctAnswer: 0, options: ['', ''] };
            else if (newType === 'short-answer') resetData = { ...resetData, marks: 2, correctAnswer: '' };
            else if (newType === 'matching') resetData = { ...resetData, marks: 4, prompts: ['Prompt 1', 'Prompt 2'], options: ['Option A', 'Option B'], correctAnswer: {} };
            else if (newType === 'mcq-grid') resetData = { ...resetData, marks: 5, rows: ['Row 1'], columns: ['Col 1'], correctAnswer: {} };
            else if (newType === 'descriptive') resetData = { ...resetData, marks: 10 };
            else if (newType === 'case-study') resetData = { ...resetData, marks: 0, subQuestions: [] }; 
            setNewQuestion(prev => ({ ...prev, ...resetData }));
        };

        const handleImageUpload = (event, field, isMultiple, subQIndex = null) => {
            const files = Array.from(event.target.files);
            if (files.length === 0) return;
            const uploadPromises = files.map(file => {
                const storageRef = ref(storage, `bank-images/${Date.now()}-${file.name}`);
                return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
            });
            Promise.all(uploadPromises).then(urls => {
                if (subQIndex !== null) {
                    // Handle Sub-Question Image Upload
                    const url = urls[0]; // Single image for sub-explanation
                    updateSubQuestion(subQIndex, field, url);
                } else {
                    if (isMultiple) updateQ(field, [...(q[field] || []), ...urls]);
                    else updateQ(field, urls[0]);
                }
            }).catch(err => setModal({ show: true, message: "Upload failed: " + err.message }));
        };

        // --- SUB-QUESTION LOGIC FOR CASE STUDY ---
        const addSubQuestion = () => {
            const emptySub = {
                id: Date.now().toString(),
                type: 'mcq',
                text: '',
                marks: 2,
                options: ['', '', '', ''],
                correctAnswer: [],
                explanation: '',
                explanationFormat: 'text'
            };
            updateQ('subQuestions', [...(q.subQuestions || []), emptySub]);
        };

        // NEW: Import Handler (Multiple)
        const handleImportSubQuestions = (importedList) => {
            const newSubQs = importedList.map(importedQ => {
                // Copy content, strip DB metadata
                const { id, topic, topicId, paperId, createdAt, updatedAt, section, ...contentData } = importedQ;
                
                return {
                    ...contentData,
                    id: Date.now().toString() + Math.random().toString(36).substr(2, 5), // Unique Temp ID
                    sourceId: id, // Track where it came from to prevent dupes in UI
                    options: contentData.options || [],
                    correctAnswer: contentData.correctAnswer || [],
                    text: contentData.text || '',
                    marks: Number(contentData.marks) || 1,
                    // --- FIX: Explicitly Import Explanation ---
                    explanation: contentData.explanation || '',
                    explanationFormat: contentData.explanationFormat || 'text'
                };
            });

            updateQ('subQuestions', [...(q.subQuestions || []), ...newSubQs]);
            setShowImportModal(false);
        };

        const removeSubQuestion = (index) => {
            const newSubs = [...q.subQuestions];
            newSubs.splice(index, 1);
            updateQ('subQuestions', newSubs);
        };

        const updateSubQuestion = (index, field, value) => {
            const newSubs = [...q.subQuestions];
            newSubs[index] = { ...newSubs[index], [field]: value };
            updateQ('subQuestions', newSubs);
        };

        return (
            <div className="p-6 border rounded-lg bg-gray-50 space-y-6 animate-fade-in relative">
                {/* Modal for Importing with Filters */}
                {showImportModal && (
                    <SubQuestionImportModal 
                        questions={questions} 
                        currentQuestionId={editingQuestionId} 
                        existingSubQuestions={q.subQuestions || []} // Pass existing ones for filtering
                        onImport={handleImportSubQuestions} // Accepts Array
                        onClose={() => setShowImportModal(false)} 
                    />
                )}

                <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="text-lg font-bold text-gray-700">
                        {editingQuestionId ? 'Edit Question in:' : 'Add Question to:'} <span className="text-purple-600">{selectedTopic.name}</span>
                    </h3>
                    <button onClick={resetEditor} className="text-gray-500 hover:text-gray-700">&times;</button>
                </div>
                
                {/* 1. Basic Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-bold mb-1 text-gray-700">Section</label>
                        <select value={q.section} onChange={(e) => updateQ('section', e.target.value)} className="w-full p-2 border rounded bg-white">
                            <option value="A">Section A</option>
                            <option value="B">Section B</option>
                            <option value="C">Section C</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-bold mb-1 text-gray-700">Type</label>
                        <select value={q.type} onChange={(e) => handleTypeChange(e.target.value)} className="w-full p-2 border rounded bg-white">
                            <option value="mcq">MCQ</option>
                            <option value="descriptive">Descriptive</option>
                            <option value="case-study">Case Study / Scenario (Grouped)</option>
                            <option value="dropdown">Drop Down</option>
                            <option value="short-answer">Short Answer</option>
                            <option value="matching">Matching</option>
                            <option value="mcq-grid">MC Grid</option>
                        </select>
                    </div>
                     <div>
                        <label className="block text-sm font-bold mb-1 text-gray-700">Marks</label>
                        <input 
                            type="number" 
                            value={q.type === 'case-study' ? (q.subQuestions || []).reduce((sum, sq) => sum + (Number(sq.marks) || 0), 0) : q.marks} 
                            onChange={(e) => updateQ('marks', e.target.value)} 
                            className={`w-full p-2 border rounded text-center ${q.type === 'case-study' ? 'bg-gray-100' : 'bg-white'}`}
                            disabled={q.type === 'case-study'} 
                        />
                    </div>
                </div>

                {/* 2. Question Content */}
                <div>
                    <label className="block text-sm font-bold mb-1 text-gray-700">
                        {q.type === 'case-study' ? 'Scenario / Case Details' : 'Question Text'}
                    </label>
                    <div className="border rounded bg-white min-h-[150px] overflow-hidden">
                        <AdvancedWordProcessor 
                            value={q.text} 
                            onChange={(val) => updateQ('text', val)} 
                        />
                    </div>
                    
                    <div className="mt-2">
                        <label className="block text-xs font-bold text-gray-500 mb-1">Question Images (Optional)</label>
                        <input type="file" onChange={(e) => handleImageUpload(e, 'images', true)} multiple className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"/>
                        <div className="flex gap-2 mt-2 flex-wrap">
                            {q.images && q.images.map((url, idx) => (
                                <div key={idx} className="relative group">
                                    <img src={url} className="h-20 w-auto border rounded bg-white" alt="Q img"/>
                                    <button onClick={() => removeItem('images', idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100">&times;</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 3. CASE STUDY SUB-QUESTIONS */}
                {q.type === 'case-study' ? (
                    <div className="bg-white p-4 rounded border border-purple-200">
                        <div className="flex justify-between items-center mb-4">
                            <h5 className="font-bold text-purple-700">Sub-Questions (Grouped)</h5>
                            <div className="flex gap-2">
                                {/* NEW: Import Button */}
                                <button 
                                    onClick={() => setShowImportModal(true)} 
                                    className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded font-bold hover:bg-blue-200 flex items-center gap-1"
                                >
                                    <span>📥</span> Import Existing
                                </button>
                                <button onClick={addSubQuestion} className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded font-bold hover:bg-purple-200">+ Add New</button>
                            </div>
                        </div>
                        
                        {(!q.subQuestions || q.subQuestions.length === 0) && (
                            <p className="text-gray-500 italic text-center p-4">No sub-questions added. Add new ones or import existing ones.</p>
                        )}

                        <div className="space-y-4">
                            {q.subQuestions && q.subQuestions.map((sq, index) => (
                                <div key={index} className="border p-4 rounded bg-gray-50 relative">
                                    <div className="flex justify-between mb-2">
                                        <div className="flex gap-2">
                                            <span className="bg-gray-200 text-xs font-bold px-2 py-1 rounded">Q{index + 1}</span>
                                            <select 
                                                value={sq.type} 
                                                onChange={(e) => updateSubQuestion(index, 'type', e.target.value)} 
                                                className="text-xs border rounded p-1"
                                            >
                                                <option value="mcq">MCQ</option>
                                                <option value="descriptive">Descriptive</option>
                                                <option value="short-answer">Short Answer</option>
                                            </select>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs text-gray-500">Marks:</span>
                                            <input 
                                                type="number" 
                                                value={sq.marks} 
                                                onChange={(e) => updateSubQuestion(index, 'marks', e.target.value)} 
                                                className="w-12 text-xs border rounded p-1 text-center"
                                            />
                                            <button onClick={() => removeSubQuestion(index)} className="text-red-500 font-bold ml-2">&times;</button>
                                        </div>
                                    </div>
                                    
                                    {/* --- FIX: Use AdvancedWordProcessor for Sub-Question Text --- */}
                                    <div className="mb-2 border rounded bg-white min-h-[100px] overflow-hidden">
                                        <AdvancedWordProcessor 
                                            value={sq.text} 
                                            onChange={(val) => updateSubQuestion(index, 'text', val)} 
                                        />
                                    </div>

                                    {/* Sub-Question Options Logic */}
                                    {sq.type === 'mcq' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {sq.options.map((opt, oIdx) => (
                                                <div key={oIdx} className="flex gap-1 items-center">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={(sq.correctAnswer || []).includes(oIdx)}
                                                        onChange={() => {
                                                            const current = sq.correctAnswer || [];
                                                            const newAns = current.includes(oIdx) ? current.filter(i=>i!==oIdx) : [...current, oIdx];
                                                            updateSubQuestion(index, 'correctAnswer', newAns);
                                                        }}
                                                        className="h-4 w-4 text-purple-600"
                                                    />
                                                    <input 
                                                        type="text" 
                                                        value={opt} 
                                                        onChange={(e) => {
                                                            const newOpts = [...sq.options];
                                                            newOpts[oIdx] = e.target.value;
                                                            updateSubQuestion(index, 'options', newOpts);
                                                        }}
                                                        className="flex-1 text-xs border rounded p-1"
                                                        placeholder={`Option ${oIdx+1}`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {sq.type === 'short-answer' && (
                                        <input 
                                            type="text" 
                                            value={sq.correctAnswer} 
                                            onChange={(e) => updateSubQuestion(index, 'correctAnswer', e.target.value)} 
                                            className="w-full text-xs border rounded p-1"
                                            placeholder="Correct Answer Text"
                                        />
                                    )}

                                    {/* --- NEW: Sub-Question Explanation Editor --- */}
                                    <div className="mt-2 pt-2 border-t text-xs">
                                        <label className="block font-bold text-gray-500 mb-1">Answer Explanation (Sub-question)</label>
                                        <div className="flex gap-2 mb-1">
                                            <button onClick={() => updateSubQuestion(index, 'explanationFormat', 'text')} className={`px-2 py-0.5 rounded ${sq.explanationFormat !== 'image' ? 'bg-gray-300 text-gray-800' : 'bg-gray-100'}`}>Text</button>
                                            <button onClick={() => updateSubQuestion(index, 'explanationFormat', 'image')} className={`px-2 py-0.5 rounded ${sq.explanationFormat === 'image' ? 'bg-gray-300 text-gray-800' : 'bg-gray-100'}`}>Image</button>
                                        </div>
                                        {sq.explanationFormat === 'image' ? (
                                            <div>
                                                <input type="file" onChange={(e) => handleImageUpload(e, 'explanation', false, index)} className="block w-full text-xs" />
                                                {sq.explanation && <img src={sq.explanation} className="mt-1 h-16 w-auto border rounded" alt="Sub Exp"/>}
                                            </div>
                                        ) : (
                                            <textarea 
                                                value={sq.explanation} 
                                                onChange={(e) => updateSubQuestion(index, 'explanation', e.target.value)} 
                                                className="w-full p-1 border rounded" 
                                                rows="2" 
                                                placeholder="Explain answer..."
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* --- Overall Answer Explanation for Case Study Scenario --- */}
                        <div className="mt-6 pt-4 border-t border-gray-300">
                            <h5 className="font-semibold text-gray-700 mb-2">Overall Case Scenario Explanation</h5>
                            <div className="flex gap-2 mb-2">
                                <button onClick={() => updateQ('explanationFormat', 'text')} className={`px-3 py-1 text-sm rounded ${q.explanationFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                <button onClick={() => updateQ('explanationFormat', 'image')} className={`px-3 py-1 text-sm rounded ${q.explanationFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                            </div>
                            {q.explanationFormat === 'text' ? (
                                <textarea value={q.explanation} onChange={(e) => updateQ('explanation', e.target.value)} className="w-full p-2 border rounded" rows="3" placeholder="Explain the main scenario logic..."/>
                            ) : (
                                <div>
                                    <input type="file" onChange={(e) => handleImageUpload(e, 'explanation', false)} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"/>
                                    {q.explanation && <img src={q.explanation} className="mt-2 h-32 w-auto border rounded" alt="Explanation"/>}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    // --- STANDARD QUESTION EDITOR ---
                    <div className="bg-white p-4 rounded border">
                        
                        {/* --- MCQ --- */}
                        {q.type === 'mcq' && (
                            <div>
                                <h5 className="font-semibold text-gray-700 mb-2">Options (Check correct answers)</h5>
                                {q.options.map((opt, idx) => (
                                    <div key={idx} className="flex items-center gap-2 mb-2">
                                        <input type="checkbox" checked={(q.correctAnswer || []).includes(idx)} onChange={() => { const current = q.correctAnswer || []; const newAns = current.includes(idx) ? current.filter(i => i !== idx) : [...current, idx]; updateQ('correctAnswer', newAns); }} className="h-5 w-5 text-purple-600"/>
                                        <input type="text" value={opt} onChange={(e) => handleArrayChange('options', idx, e.target.value)} className="flex-1 p-2 border rounded" placeholder={`Option ${idx + 1}`} />
                                        <button onClick={() => removeItem('options', idx)} className="text-red-500 px-2">&times;</button>
                                    </div>
                                ))}
                                <button onClick={() => addItem('options')} className="text-sm text-purple-600 font-bold">+ Add Option</button>
                            </div>
                        )}

                        {/* --- Dropdown --- */}
                        {q.type === 'dropdown' && (
                            <div>
                                <h5 className="font-semibold text-gray-700 mb-2">Options (Select correct answer)</h5>
                                {q.options.map((opt, idx) => (
                                    <div key={idx} className="flex items-center gap-2 mb-2">
                                        <input type="radio" name="dd-correct" checked={q.correctAnswer === idx} onChange={() => updateQ('correctAnswer', idx)} className="h-5 w-5 text-purple-600"/>
                                        <input type="text" value={opt} onChange={(e) => handleArrayChange('options', idx, e.target.value)} className="flex-1 p-2 border rounded" placeholder={`Option ${idx + 1}`} />
                                        <button onClick={() => removeItem('options', idx)} className="text-red-500 px-2">&times;</button>
                                    </div>
                                ))}
                                <button onClick={() => addItem('options')} className="text-sm text-purple-600 font-bold">+ Add Option</button>
                            </div>
                        )}

                        {/* --- Short Answer --- */}
                        {q.type === 'short-answer' && (
                            <div>
                                <h5 className="font-semibold text-gray-700 mb-2">Correct Answer</h5>
                                <input type="text" value={q.correctAnswer} onChange={(e) => updateQ('correctAnswer', e.target.value)} className="w-full p-2 border rounded" placeholder="Enter the exact answer text" />
                            </div>
                        )}

                        {/* --- Matching --- */}
                        {q.type === 'matching' && (
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Prompts</h5>
                                    {q.prompts.map((p, idx) => (<div key={idx} className="flex gap-2 mb-2"><input type="text" value={p} onChange={(e) => handleArrayChange('prompts', idx, e.target.value)} className="w-full p-2 border rounded" /><button onClick={() => removeItem('prompts', idx)} className="text-red-500">&times;</button></div>))}
                                    <button onClick={() => addItem('prompts')} className="text-sm text-purple-600 font-bold">+ Add Prompt</button>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-gray-700 mb-2">Options</h5>
                                    {q.options.map((o, idx) => (<div key={idx} className="flex gap-2 mb-2"><input type="text" value={o} onChange={(e) => handleArrayChange('options', idx, e.target.value)} className="w-full p-2 border rounded" /><button onClick={() => removeItem('options', idx)} className="text-red-500">&times;</button></div>))}
                                    <button onClick={() => addItem('options')} className="text-sm text-purple-600 font-bold">+ Add Option</button>
                                </div>
                                <div className="col-span-2 mt-4 pt-4 border-t">
                                    <h5 className="font-semibold text-gray-700 mb-2">Define Correct Pairs</h5>
                                    {q.prompts.map((p, pIdx) => (<div key={pIdx} className="flex items-center gap-4 mb-2 p-2 bg-gray-50 rounded"><span className="flex-1 font-medium">{p}</span><span>&rarr;</span><select value={q.correctAnswer[pIdx] ?? ''} onChange={(e) => { const val = e.target.value === '' ? undefined : parseInt(e.target.value); const newCA = { ...q.correctAnswer }; if (val === undefined) delete newCA[pIdx]; else newCA[pIdx] = val; updateQ('correctAnswer', newCA); }} className="flex-1 p-2 border rounded"><option value="">Select Match...</option>{q.options.map((opt, oIdx) => (<option key={oIdx} value={oIdx}>{opt}</option>))}</select></div>))}
                                </div>
                            </div>
                        )}

                        {/* --- MC Grid --- */}
                        {q.type === 'mcq-grid' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-6">
                                    <div><h5 className="font-semibold text-gray-700 mb-2">Rows</h5>{q.rows.map((r, idx) => (<div key={idx} className="flex gap-2 mb-2"><input type="text" value={r} onChange={(e) => handleArrayChange('rows', idx, e.target.value)} className="w-full p-2 border rounded" /><button onClick={() => removeItem('rows', idx)} className="text-red-500">&times;</button></div>))}<button onClick={() => addItem('rows')} className="text-sm text-purple-600 font-bold">+ Add Row</button></div>
                                    <div><h5 className="font-semibold text-gray-700 mb-2">Columns</h5>{q.columns.map((c, idx) => (<div key={idx} className="flex gap-2 mb-2"><input type="text" value={c} onChange={(e) => handleArrayChange('columns', idx, e.target.value)} className="w-full p-2 border rounded" /><button onClick={() => removeItem('columns', idx)} className="text-red-500">&times;</button></div>))}<button onClick={() => addItem('columns')} className="text-sm text-purple-600 font-bold">+ Add Column</button></div>
                                </div>
                                <div className="pt-4 border-t"><h5 className="font-semibold text-gray-700 mb-2">Correct Grid Answers</h5><div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr><th className="p-2"></th>{q.columns.map((c, cIdx) => <th key={cIdx} className="p-2">{c}</th>)}</tr></thead><tbody>{q.rows.map((r, rIdx) => (<tr key={rIdx} className="border-t"><td className="p-2 font-bold">{r}</td>{q.columns.map((c, cIdx) => (<td key={cIdx} className="p-2 text-center"><input type="radio" name={`grid-row-${rIdx}`} checked={q.correctAnswer[rIdx] === cIdx} onChange={() => { const newCA = { ...q.correctAnswer, [rIdx]: cIdx }; updateQ('correctAnswer', newCA); }} /></td>))}</tr>))}</tbody></table></div></div>
                            </div>
                        )}

                        {/* --- Descriptive --- */}
                        {q.type === 'descriptive' && (
                            <div className="space-y-4">
                                <StarterSpreadsheetEditor value={q.starterSpreadsheet} onChange={value=>updateQ('starterSpreadsheet',value)} marks={q.marks} onMarksChange={value=>updateQ('marks',value)}/>
                                <h5 className="font-bold text-gray-700">Suggested Answer & Grading</h5>
                                <div className="border p-4 rounded-lg bg-gray-50">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Suggested Answer (Text/Word)</label>
                                    <div className="h-[500px] border rounded overflow-hidden bg-white">
                                        <AdvancedWordProcessor value={q.suggestedAnswerContent || ''} onChange={(val) => updateQ('suggestedAnswerContent', val)} />
                                    </div>
                                </div>
                                
                                <div className="border p-4 rounded-lg bg-gray-50">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Suggested Answer (Spreadsheet)</label>
                                    <div className="h-[500px] border rounded overflow-hidden bg-white">
                                        <CustomSpreadsheet data={q.suggestedAnswerExcel} onChange={(val) => updateQ('suggestedAnswerExcel', val)} />
                                    </div>
                                </div>
                                
                                <div className="border p-4 rounded-lg bg-gray-50">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Marking Scheme</label>
                                    <div className="h-[500px] border rounded overflow-hidden bg-white">
                                        <AdvancedWordProcessor value={q.markingSchemeContent || ''} onChange={(val) => updateQ('markingSchemeContent', val)} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- Explanation --- */}
                        {q.type !== 'descriptive' && (
                             <div className="mt-6 pt-4 border-t border-gray-300">
                                <h5 className="font-semibold text-gray-700 mb-2">Answer Explanation</h5>
                                <div className="flex gap-2 mb-2"><button onClick={() => updateQ('explanationFormat', 'text')} className={`px-3 py-1 text-sm rounded ${q.explanationFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button><button onClick={() => updateQ('explanationFormat', 'image')} className={`px-3 py-1 text-sm rounded ${q.explanationFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button></div>
                                {q.explanationFormat === 'text' ? (<textarea value={q.explanation} onChange={(e) => updateQ('explanation', e.target.value)} className="w-full p-2 border rounded" rows="3" placeholder="Explain the correct answer..."/>) : (<div><input type="file" onChange={(e) => handleImageUpload(e, 'explanation', false)} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"/>{q.explanation && <img src={q.explanation} className="mt-2 h-32 w-auto border rounded" alt="Explanation"/>}</div>)}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={resetEditor} className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400">Cancel</button>
                    <button onClick={handleSaveQuestion} className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:from-purple-700 hover:to-pink-700">
                        {editingQuestionId ? 'Update Question' : 'Save to Bank'}
                    </button>
                </div>
            </div>
        );
    };

    // --- Sub-Component: Render Topic Grid ---
    const renderTopicList = () => (
        <div className="space-y-6 animate-fade-in">
            {/* Create Topic Form */}
            <div className="bg-gray-50 p-4 rounded-lg border flex flex-col md:flex-row gap-4 items-end md:items-center">
                <div className="flex-grow w-full">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Create New Topic Folder</label>
                    <div className="flex gap-2">
                        <input 
                            type="text" 
                            value={newTopicName} 
                            onChange={(e) => setNewTopicName(e.target.value)} 
                            placeholder="e.g., Non-Current Assets"
                            className="flex-1 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <button onClick={handleCreateTopic} disabled={!newTopicName.trim()} className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700 disabled:opacity-50">
                            Create
                        </button>
                    </div>
                </div>
            </div>

            {/* Topic Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {isLoading ? (
        <div className="col-span-full">
            <Skeleton type="folder" count={3} />
        </div>
    ) : topics.length === 0 ? (
                    <div className="col-span-full text-center py-12 bg-white rounded border border-dashed text-gray-500">
                        <p className="text-lg">No topics found.</p>
                        <p className="text-sm">Create a topic above to start adding questions.</p>
                    </div>
                ) : (
                    topics.map(topic => {
                        const questionCount = questions.filter(q => q.topicId === topic.id).length;
                        return (
                            <div 
                                key={topic.id} 
                                onClick={() => handleEnterTopic(topic)} 
                                className="bg-white p-6 rounded-lg shadow border cursor-pointer group relative flex flex-col items-center justify-center text-center h-40 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:bg-purple-50 hover:border-purple-200"
                            >
                                <div className="absolute top-2 left-2 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded-full">
                                    {questionCount} Questions
                                </div>

                                <span className="text-4xl mb-2 group-hover:scale-110 transition-transform">📂</span>
                                <h3 className="font-bold text-lg text-gray-800 group-hover:text-purple-700">{topic.name}</h3>
                                <span className="text-xs text-gray-400 mt-2">Click to open</span>
                                
                                <button 
                                    onClick={(e) => handleDeleteTopic(topic.id, e)}
                                    className="absolute top-2 right-2 text-gray-300 hover:text-red-500 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete Topic"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    // --- Sub-Component: Render Question List ---
    const renderQuestionList = () => {
        const topicQuestions = questions.filter(q => q.topicId === selectedTopic.id);
        const filteredQuestions = topicQuestions.filter(q => {
            if (!filterSection) return true;
            return q.section === filterSection;
        });

        return (
            <div className="space-y-4 animate-fade-in">
                 <div className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50 rounded border mb-4 items-center">
                    <div className="flex-1 flex items-center gap-2 text-lg">
                        <button onClick={() => setViewMode('topics')} className="text-purple-600 hover:underline font-bold">Topics</button>
                        <span className="text-gray-400">/</span>
                        <h3 className="font-bold text-gray-800 bg-white px-2 py-1 rounded shadow-sm">📂 {selectedTopic.name}</h3>
                    </div>
                    <div className="w-full md:w-48">
                        <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)} className="w-full p-2 border rounded bg-white focus:outline-none focus:ring-1 focus:ring-purple-500">
                            <option value="">All Sections</option>
                            <option value="A">Section A</option>
                            <option value="B">Section B</option>
                            <option value="C">Section C</option>
                        </select>
                    </div>
                </div>

                {filteredQuestions.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded border border-dashed">
                        <p className="text-gray-500 mb-4">No questions in this topic yet.</p>
                        <button onClick={() => setIsCreating(true)} className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-bold hover:bg-purple-200">
                            + Add First Question
                        </button>
                    </div>
                ) : (
                    filteredQuestions.map((q) => (
                        <div key={q.id} className="p-4 border rounded hover:bg-gray-50 flex justify-between items-start transition-colors bg-white">
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className={`px-2 py-0.5 text-xs font-bold rounded uppercase 
                                        ${q.type === 'descriptive' ? 'bg-purple-100 text-purple-800' : 
                                          q.type === 'mcq' ? 'bg-blue-100 text-blue-800' : 
                                          q.type === 'case-study' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {q.type.replace('-', ' ')}
                                    </span>
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded">Sec {q.section}</span>
                                    <span className="text-xs text-gray-500">({q.marks} marks)</span>
                                </div>
                                
                                {q.type === 'case-study' ? (
                                    // --- FIX: Cleaner Case Study Summary ---
                                    <div className="bg-gray-50 p-2 rounded border border-gray-200">
                                        <div 
                                            className="text-gray-800 text-sm mb-2 line-clamp-2 prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: q.text || '<i>(Scenario description...)</i>' }}
                                        />
                                        <div className="text-xs text-purple-600 font-bold bg-purple-50 inline-block px-2 py-1 rounded">
                                            {q.subQuestions?.length || 0} Sub-Questions Linked
                                        </div>
                                    </div>
                                ) : (
                                    // --- Standard Question Display ---
                                    <div 
                                        className="text-gray-800 line-clamp-2 prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: q.text || '(Image-based question)' }}
                                    />
                                )}
                            </div>
                            
                            <div className="flex gap-2 ml-4">
                                <button 
                                    onClick={() => handleEditQuestion(q)} 
                                    className="text-blue-500 hover:text-blue-700 text-sm font-semibold p-2 hover:bg-blue-50 rounded"
                                >
                                    Edit
                                </button>
                                <button 
                                    onClick={() => handleDeleteQuestion(q.id)} 
                                    className="text-red-500 hover:text-red-700 text-sm font-semibold p-2 hover:bg-red-50 rounded"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow min-h-[500px]">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 border-b pb-4">
                <div>
                    <button onClick={onBack} className="text-sm text-purple-600 hover:underline mb-2">&larr; Back to Papers</button>
                    <h2 className="text-2xl font-bold text-gray-800">
                        Question Bank: {paper.name}
                    </h2>
                </div>
                {/* Show "Add Question" button only when inside a topic and not creating/editing */}
                {viewMode === 'questions' && !isCreating && (
                    <button onClick={() => setIsCreating(true)} className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-purple-700 shadow flex items-center gap-2">
                        <span className="text-xl">+</span> Add Question
                    </button>
                )}
            </div>

            {/* Views */}
            {viewMode === 'topics' && renderTopicList()}
            {viewMode === 'questions' && (
                isCreating ? renderQuestionEditor() : renderQuestionList()
            )}
        </div>
    );
};
export default QuestionBankManager;